import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { connect, useSelector } from 'react-redux';
import { compose } from 'redux';
import { Link, useHistory, useLocation } from 'react-router-dom';
import { Portal } from 'react-portal';
import Helmet from '@plone/volto/helpers/Helmet/Helmet';
import { Container, Segment, Button, Message, Tab } from 'semantic-ui-react';
import { defineMessages, injectIntl } from 'react-intl';
import Error from '@plone/volto/components/theme/Error/Error';
import Icon from '@plone/volto/components/theme/Icon/Icon';
import Toolbar from '@plone/volto/components/manage/Toolbar/Toolbar';
import backSVG from '@plone/volto/icons/back.svg';
import {
  getRezeptByID,
  deleteRezept,
  deleteRezeptForce,
  getRezeptNutrientsByID,
  getRohstoffKostenByID,
  getVerkaufsinformationByID,
  createVerkaufsinformation,
  deleteVerkaufsinformation,
  getVistPermissions,
} from '../../../actions';
import {
  canCrudThisRezept,
  canReadRezepte,
  selectIsAdmin,
} from '../../../helpers/permissions';
import NavHeader from '../NavHeader';
import ButtonArea from '../ButtonArea';
import NextPreviousButtons from '../NextPreviousButtons';
import NutrientDetails from './Details/NutrientDetails';
import ResouceCosts from './Details/ResourceCosts';
import GeneralDetails from './Details/GeneralDetails';
import VerkaufsinformationDetails from './Details/Verkaufsinformation/VerkaufsinformationDetails';
import RezeptVersionHistory from './RezeptVersionHistory';

const messages = defineMessages({
  back: {
    id: 'Back',
    defaultMessage: 'Back',
  },
});

const RezepteDetails = ({
  pathname,
  rezept,
  rezeptNutrients,
  rohstoffKosten,
  getRezeptNutrientsByID,
  getRohstoffKostenByID,
  getRezeptByID,
  deleteRezept,
  deleteRezeptForce,
  getVerkaufsinformationByID,
  verkaufsinformation,
  deleteVerkaufsinformation,
  createVerkaufsinformation,
  intl,
  token,
  permissions = null,
  getVistPermissions,
  rezepte_list,
  batching_params,
}) => {
  const history = useHistory();
  const location = useLocation();
  const isAdmin = useSelector(selectIsAdmin);
  const [error, setError] = useState(null);
  const [errorDetail, setErrorDetail] = useState(null);
  const [isClient, setIsClient] = useState(false);
  const [deleteSuccess, setDeleteSuccess] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);
  const editURL =
    activeIndex === 3
      ? '/controlpanel/rezeptdatenbank/rezepte/verkaufsinformation/'
      : '';
  const editName = activeIndex === 3 ? 'Verkaufsinformation' : 'Rezept';
  const [numberOfPieces, setNumberOfPieces] = useState(1);
  // Read access gates the detail view. Edit/delete are gated per-recipe:
  // visible iff at least one of THIS recipe's Rezeptgruppen is in the
  // user's CRUD set (mirrors backend ensure_can_crud_existing_rezept).
  // Without this gate, e.g. MK opening a Grundrezepte-only recipe would
  // see Edit and only fail at save time.
  const canViewDetails = canReadRezepte(permissions);
  const showEditButton = canCrudThisRezept(permissions, rezept);
  const showDeleteButton = canCrudThisRezept(permissions, rezept);
  // flag to make sure permissions are fully loaded before returning
  // an unauthorized error
  const [permissionsLoading, setPermissionsLoading] = useState(true);

  const searchParams = new URLSearchParams(location.search);
  const id = searchParams.get('id');

  // load permissions
  useEffect(() => {
    setIsClient(true);
    // Fetch @vist_permissions if the reducer hasn't seen a response yet.
    if (!permissions && permissionsLoading) {
      getVistPermissions()
        .then(() => {
          setPermissionsLoading(false);
        })
        .catch(() => {
          setError({ status: 401 });
          setPermissionsLoading(false);
        });
    }
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    if (id) {
      setIsClient(true);
      getRohstoffKostenByID(id, numberOfPieces);
      getRezeptByID(id);
      getRezeptNutrientsByID(id);
      getVerkaufsinformationByID(id);
    }
    // eslint-disable-next-line
  }, [getRezeptByID, getRezeptNutrientsByID, id]);

  useEffect(() => {
    if (id) {
      getRohstoffKostenByID(id, numberOfPieces);
    }
  }, [numberOfPieces, id, getRohstoffKostenByID]);

  const handleDelete = async (options = {}) => {
    const searchParams = new URLSearchParams(location.search);
    const id = searchParams.get('id');

    const { force } = options;
    try {
      if (force) {
        await deleteRezeptForce(id);
      } else {
        await deleteRezept(id);
      }
      setDeleteSuccess(true);
    } catch (error) {
      // Referenced by
      if (error.response?.body?.error?.type === 'Referenced by rezepte') {
        setError('Kann nicht gelöscht werden');
        setErrorDetail(
          'Wird referenziert von Rezepten ' +
            error.response?.body?.error?.message,
        );
      } else {
        // Handle delete error
        setError(error.name);
      }
    }
  };

  const handleDeleteVerkaufsinformation = async (options = {}) => {
    const searchParams = new URLSearchParams(location.search);
    const id = searchParams.get('id');

    try {
      await deleteVerkaufsinformation(id);
      getVerkaufsinformationByID(id);
    } catch (error) {
      // Handle delete error
      setError(error.name);
      setErrorDetail(error.name + ': ' + error.message);
    }
  };

  const addVerkaufsinformation = () => {
    if (id) {
      const verkaufsinformation_data = {
        rezepte_id: id,
        deleted: 0,
        hauptbestandteile: '',
        merkmale: '',
        lagerung: '',
        verzehrtipps: '',
        hinweise: '',
      };

      createVerkaufsinformation(verkaufsinformation_data)
        .then((result) => {
          const verkaufsinformation_id = result.id;
          if (verkaufsinformation_id) {
            history.push(
              `/controlpanel/rezeptdatenbank/rezepte/verkaufsinformation/edit?id=${id}`,
            );
          } else {
            setError(
              error.name +
                ': Es ist etwas schiefgelaufen, Verkaufsinformation wurde nicht angelegt.',
            );
          }
        })
        .catch((error) => {
          setError(
            error.name +
              ': Es ist etwas schiefgelaufen, Verkaufsinformation wurde nicht angelegt',
          );
          setErrorDetail(error.name + ': ' + error.message);
        });
    }
  };

  const panes = [
    {
      menuItem: 'Rezept',
      render: () => (
        <Tab.Pane>{rezept && <GeneralDetails rezept={rezept} />}</Tab.Pane>
      ),
    },
    {
      menuItem: 'Nährwerte',
      render: () => (
        <Tab.Pane>
          {rezeptNutrients && (
            <NutrientDetails
              rezeptNutrients={rezeptNutrients}
              siedefetttemperatur={rezept?.siedefetttemperatur}
            />
          )}
        </Tab.Pane>
      ),
    },
    {
      menuItem: 'Rohstoffkosten',
      render: () => (
        <Tab.Pane>
          {rohstoffKosten && (
            <ResouceCosts
              rohstoffKosten={rohstoffKosten}
              numberOfPieces={numberOfPieces}
              setNumberOfPieces={setNumberOfPieces}
            />
          )}
        </Tab.Pane>
      ),
    },
    {
      menuItem: 'Verkaufsinformation',
      render: () => (
        <Tab.Pane>
          {verkaufsinformation.id ? (
            <VerkaufsinformationDetails
              verkaufsinformation={verkaufsinformation}
            />
          ) : (
            <div className="sales-info-add">
              <h2>Noch keine Verkaufsinformation vorhanden</h2>
              <Button onClick={addVerkaufsinformation}>
                Verkaufsinformation Anlegen
              </Button>
            </div>
          )}
        </Tab.Pane>
      ),
    },
  ];

  if (!token) {
    return <Error error={{ status: 401 }} />;
  }
  if (!permissionsLoading && !canViewDetails) {
    return <Error error={{ status: 401 }} />;
  }

  return (
    <div className="view-wrapper">
      <Helmet title={rezept?.name} />
      <Container className="controlpanel">
        <NavHeader />
        <Segment.Group raised>
          <Segment className="primary">
            <div className="rezeptdatenbank header">
              <div className="header-container">
                <h1> {rezept && rezept.name}</h1>
                <h2>{rezept && rezept.verkehrsbezeichnung}</h2>
              </div>
              {rezept.image?.['@id'] && (
                <img
                  className="recipe-image"
                  alt=""
                  src={`${rezept.image['@id']}/@@images/image`}
                />
              )}
              {rezept.intern_extern === false && (
                <p>
                  <a
                    target="_blank"
                    rel="noreferrer"
                    href={`/alle-rezepte/${id}`}
                  >
                    Rezept auf Website
                  </a>
                </p>
              )}
              <h2>Downloads</h2>
              <a
                target="_blank"
                rel="noreferrer"
                href={`/++api++/@@rezept-pdf?id=${rezept.id}`}
              >
                Rezept PDF
              </a>
              <br />
              <a
                target="_blank"
                rel="noreferrer"
                href={`/++api++/@@rezept-xls?id=${rezept.id}`}
              >
                Rezept Excel
              </a>
              <br />
              <a
                target="_blank"
                rel="noreferrer"
                href={`/++api++/@@rezept-pdf?id=${rezept.id}&naehrwerte=1`}
              >
                Nährwerte
              </a>
              <div className="button-container">
                <NextPreviousButtons
                  currentItem={id}
                  itemlist={rezepte_list.rezepte}
                  totalPages={rezepte_list.total_pages}
                  batching_params={batching_params}
                />
                <ButtonArea
                  isDetails={true}
                  id={rezept.id}
                  editURL={editURL}
                  editName={editName}
                  handleDelete={
                    activeIndex == 3
                      ? handleDeleteVerkaufsinformation
                      : handleDelete
                  }
                  backURL={`/controlpanel/rezeptdatenbank/rezepte`}
                  showDeleteButton={showDeleteButton}
                  showEditButton={showEditButton}
                  references={rezept?.rezepte_ids || []}
                  typeString={
                    activeIndex == 3 ? 'die Verkaufsinformation' : 'das Rezept'
                  }
                >
                  <RezeptVersionHistory
                    rezeptId={rezept.id}
                    canRestore={isAdmin}
                  />
                </ButtonArea>
              </div>
            </div>
          </Segment>
          {error && (
            <Message negative>
              <Message.Header>Error</Message.Header>
              <p>
                {error.message} {errorDetail}
              </p>
            </Message>
          )}
          {deleteSuccess ? (
            <Message positive>
              <Message.Header>Rezept wurde gelöscht</Message.Header>
              <Button
                primary
                as={Link}
                to="/controlpanel/rezeptdatenbank/rezepte"
              >
                Zurück zur Rezepte-Übersicht
              </Button>
            </Message>
          ) : (
            <>
              {rezept && (
                <Tab
                  menu={{ secondary: true, pointing: true }}
                  panes={panes}
                  activeIndex={activeIndex}
                  onTabChange={(e, { activeIndex }) => {
                    e.preventDefault();
                    setActiveIndex(activeIndex);
                  }}
                />
              )}
            </>
          )}
        </Segment.Group>
      </Container>
      {isClient && (
        <Portal node={document.getElementById('toolbar')}>
          <Toolbar
            pathname={pathname}
            hideDefaultViewButtons
            inner={
              <Link to="/controlpanel/rezeptdatenbank" className="item">
                <Icon
                  name={backSVG}
                  className="contents circled"
                  size="30px"
                  title={intl.formatMessage(messages.back)}
                />
              </Link>
            }
          />
        </Portal>
      )}
    </div>
  );
};

RezepteDetails.propTypes = {
  pathname: PropTypes.string.isRequired,
  rezept: PropTypes.object.isRequired,
  rezeptNutrients: PropTypes.object.isRequired,
  getRezeptByID: PropTypes.func.isRequired,
  deleteRezept: PropTypes.func.isRequired,
  intl: PropTypes.object.isRequired,
  verkaufsinformation: PropTypes.oneOfType([PropTypes.object, PropTypes.array])
    .isRequired,
  getVerkaufsinformationByID: PropTypes.func.isRequired,
  createVerkaufsinformation: PropTypes.func.isRequired,
  token: PropTypes.string,
  permissions: PropTypes.object,
  getVistPermissions: PropTypes.func,
};

export default compose(
  injectIntl,
  connect(
    (state) => ({
      rezepte_list: state.rezepte.data,
      batching_params: state.batchingParameters,
      pathname: state.router.location.pathname,
      rezept: state.rezeptByID.data,
      rezeptNutrients: state.rezeptNutrients.data,
      rohstoffKosten: state.rohstoffKosten.data,
      verkaufsinformation: state.verkaufsinformationByID.data,
      token: state.userSession.token,
      permissions: state.permissions.permissions,
    }),
    {
      getRezeptByID,
      deleteRezept,
      deleteRezeptForce,
      getRezeptNutrientsByID,
      getRohstoffKostenByID,
      getVerkaufsinformationByID,
      deleteVerkaufsinformation,
      createVerkaufsinformation,
      getVistPermissions,
    },
  ),
)(RezepteDetails);
